import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsService } from '../services';
import { useAuth } from './AuthContext';

const SettingsContext = createContext({
  settings: null,
  currencySymbol: '£',
  region: 'UK',
  showBankHolidays: true,
  forecastAllocationDays: 'first_day',
  forecastAllocationAuditors: 'equal_auditors',
  loading: true,
  error: null,
  formatCurrency: (amount) => `£${amount}`,
  refreshSettings: () => {}
});

export const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // iter448 — Per-session region toggle. Persisted in localStorage so
  // it survives reloads but is scoped to THIS browser session, not the
  // shared OrganisationSettings row. Falls back to org default when
  // the user has never touched the toggle.
  const [sessionRegion, setSessionRegionState] = useState(() => {
    try { return localStorage.getItem('session_region') || null; } catch { return null; }
  });

  const fetchSettings = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const data = await settingsService.get();
      setSettings(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError(err);
      // Default settings on error
      setSettings({
        default_region: 'UK',
        currency_symbol: '£',
        show_bank_holidays: true,
        forecast_allocation_days: 'first_day',
        forecast_allocation_auditors: 'equal_auditors'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [user]);

  // iter448 — sessionRegion (per-tab toggle) beats org default_region.
  const region = sessionRegion || settings?.default_region || 'UK';
  // Derive currency symbol from region
  const currencySymbol = region === 'UAE' ? 'AED' : '£';
  const showBankHolidays = settings?.show_bank_holidays ?? true;
  const forecastAllocationDays = settings?.forecast_allocation_days || 'first_day';
  const forecastAllocationAuditors = settings?.forecast_allocation_auditors || 'equal_auditors';

  // iter448 — the toggle now writes to localStorage only. It no longer
  // mutates the shared OrganisationSettings row, so admins on different
  // regions no longer clobber each other's toggle. The X-Region header
  // (added by services/api.js) ring-fences data for THIS session.
  const setRegion = async (newRegion) => {
    if (newRegion === region) return;
    const cleaned = (newRegion === 'UK' || newRegion === 'UAE') ? newRegion : null;
    try {
      if (cleaned) {
        localStorage.setItem('session_region', cleaned);
      } else {
        localStorage.removeItem('session_region');
      }
      setSessionRegionState(cleaned);
    } catch (err) {
      console.error('Failed to update region:', err);
      throw err;
    }
  };

  const formatCurrency = (amount, decimals = 2) => {
    if (amount === null || amount === undefined) return '-';
    const num = parseFloat(amount);
    if (isNaN(num)) return '-';
    
    // Round to specified decimal places using standard rounding (half-up)
    const rounded = Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    
    // For AED, show as "AED 1,000.00" not "AED1,000.00"
    if (currencySymbol === 'AED') {
      return `AED ${rounded.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
    }
    
    return `${currencySymbol}${rounded.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      currencySymbol,
      region,
      showBankHolidays,
      forecastAllocationDays,
      forecastAllocationAuditors,
      loading,
      error,
      formatCurrency,
      setRegion,
      refreshSettings: fetchSettings
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
